import { Section, SectionHeading } from "@/components/Section";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Section className="pt-28 pb-12">
        <SectionHeading
          eyebrow="Legal"
          title="Privacy Policy"
          intro={`This policy describes how Nodera Health, Inc. ("Nodera", "we", "us") collects, uses, and protects information when you interact with our website, request a demo, or use our operational infrastructure for healthcare clinics.`}
          align="center"
        />
        <div className="mt-2 text-center text-xs uppercase tracking-[0.18em] font-semibold text-slate-400">
          Effective February 26, 2026
        </div>
      </Section>

      <Section className="pt-0 pb-24">
        <article className="mx-auto max-w-3xl prose prose-slate prose-headings:font-display prose-headings:text-slate-900 prose-h2:mt-12 prose-h2:mb-3 prose-h2:text-xl prose-p:text-slate-700 prose-li:text-slate-700">
          <h2>1. What we collect</h2>
          <p>
            We collect only the information you provide to us directly and the minimum operational
            data needed to deliver our service:
          </p>
          <ul>
            <li>
              <strong>Account &amp; contact data</strong> — name, work email, role, clinic name, phone
              (when you request access or sign in to the clinic / vendor portal).
            </li>
            <li>
              <strong>Operational data</strong> — workflow records, vendor activations, program
              configurations, and audit logs created in the course of using the platform.
            </li>
            <li>
              <strong>Usage telemetry</strong> — page views, feature interactions, and email open /
              click events (used to improve the product and detect drift).
            </li>
          </ul>
          <p>
            Nodera does <strong>not</strong> collect protected health information (PHI) directly.
            Clinics retain ownership and control of any patient data they choose to attach to a
            workflow or roster record.
          </p>

          <h2>2. How we use it</h2>
          <ul>
            <li>To provide, secure, and improve the Nodera platform.</li>
            <li>To send transactional emails (login links, workflow updates, billing receipts) via Resend.</li>
            <li>To send opt-in marketing communications (you can unsubscribe at any time from the footer of any email).</li>
            <li>To detect operational drift and notify your admins via the daily digest.</li>
          </ul>

          <h2>3. Sub-processors</h2>
          <p>
            We rely on a small set of vetted sub-processors. Each is contractually bound to the same
            confidentiality standards Nodera operates under:
          </p>
          <ul>
            <li><strong>MongoDB Atlas</strong> — primary database, US-east region, encryption at rest.</li>
            <li><strong>Resend</strong> — transactional + digest email delivery.</li>
            <li><strong>Stripe</strong> — payments and billing (we never store full card data).</li>
            <li><strong>Anthropic Claude</strong> — AI summarization for vendor applications and intelligence reports.</li>
            <li><strong>Emergent</strong> — Kubernetes-based hosting infrastructure.</li>
          </ul>

          <h2>4. Data retention</h2>
          <p>
            Operational data is retained for the duration of your subscription and for 90 days after
            cancellation, after which it is permanently deleted unless a longer retention period is
            legally required (e.g. financial records held for tax compliance).
          </p>

          <h2>5. Your rights</h2>
          <p>
            You can request a copy, export, or deletion of your account data at any time by emailing{" "}
            <a href="mailto:info@noderahealth.com" className="text-blue-700 hover:underline">
              info@noderahealth.com
            </a>
            . We respond within 30 days.
          </p>

          <h2>6. Security</h2>
          <p>
            All traffic is encrypted in transit (TLS 1.2+). Database access is gated by role-based
            authentication, and admin actions are logged. We do not store passwords in plain text —
            password hashes use bcrypt with per-user salts.
          </p>

          <h2>7. Children</h2>
          <p>
            Nodera is a B2B platform sold to licensed healthcare clinics. We do not knowingly
            collect data from anyone under 18.
          </p>

          <h2>8. Changes to this policy</h2>
          <p>
            We will post the effective date of any material change at the top of this page and, where
            appropriate, notify account admins via email.
          </p>

          <h2>9. Contact</h2>
          <p>
            Questions, requests, or concerns:{" "}
            <a href="mailto:info@noderahealth.com" className="text-blue-700 hover:underline">
              info@noderahealth.com
            </a>
            .
          </p>

          <hr className="my-10 border-slate-200" />
          <p className="text-sm">
            <Link
              to="/"
              data-testid="privacy-back-home"
              className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Nodera Health
            </Link>
          </p>
        </article>
      </Section>
    </div>
  );
}
